/**
 * Configuration des documents non modifiables - CORE-12
 *
 * Ce fichier centralise la configuration des documents non modifiables.
 * Pour ajouter/retirer un type, modifier uniquement ce fichier.
 */

// Types de documents non modifiables apres premier upload
// Table: document_entreprise_meta
export const NON_MODIFIABLE_DOCUMENT_TYPES = [
  "rib",
  "declaration_honneur",
  "kbis",
] as const;

// Types de documents non modifiables pour Accord Cadre
// Table: document_accord_cadre_meta
export const NON_MODIFIABLE_ACCORD_CADRE_TYPES = [
  "cv_equipe",
  "contrat_centralis",
] as const;

// Tous les types non modifiables (union des deux)
export const ALL_NON_MODIFIABLE_TYPES = [
  ...NON_MODIFIABLE_DOCUMENT_TYPES,
  ...NON_MODIFIABLE_ACCORD_CADRE_TYPES,
] as const;

// Documents generes par Centralis (jamais uploadables par les utilisateurs)
export const CENTRALIS_GENERATED_TYPES = ["contrat_centralis"] as const;

// Roles autorises a modifier les documents non modifiables
export const CENTRALIS_MODIFICATION_ROLES = [
  "centralis_admin",
  "centralis_tech",
  "centralis_dev",
] as const;

// Messages
export const DOCUMENT_MESSAGES = {
  NON_MODIFIABLE:
    " Ce document n’est modifiable qu’en en faisant la demande par écrit auprès de Centralis.",
  CENTRALIS_GENERATED: "Document genere par Centralis",
  GARANTIE_DECENNALE: "Pour les entreprises non soumises, chargez votre RCP.",
} as const;

// Mapping des labels vers les codes techniques (pour Accord Cadre)
// Les documents AC utilisent des labels comme type, pas des codes
const DOCUMENT_TYPE_MAPPING: Record<string, string> = {
  // CV equipe
  "cv de l'equipe encadrante": "cv_equipe",
  "cv de l'équipe encadrante": "cv_equipe",
  "cv equipe": "cv_equipe",
  cv_equipe: "cv_equipe",
  // Contrat Centralis
  "contrat centralis": "contrat_centralis",
  "contrat centralis (ccap+ae)": "contrat_centralis",
  contrat_centralis: "contrat_centralis",
  // RIB
  rib: "rib",
  // KBIS
  kbis: "kbis",
  // Declaration honneur
  "declaration sur l'honneur": "declaration_honneur",
  "déclaration sur l'honneur": "declaration_honneur",
  declaration_honneur: "declaration_honneur",
};

/**
 * Normalise un type de document vers son code technique
 */
export const normalizeDocumentType = (type: string): string => {
  const lowerType = type.toLowerCase();
  return DOCUMENT_TYPE_MAPPING[lowerType] || lowerType;
};

// Types TypeScript
export type NonModifiableDocumentType =
  (typeof NON_MODIFIABLE_DOCUMENT_TYPES)[number];
export type NonModifiableAccordCadreType =
  (typeof NON_MODIFIABLE_ACCORD_CADRE_TYPES)[number];
export type AllNonModifiableType = (typeof ALL_NON_MODIFIABLE_TYPES)[number];
export type CentralisGeneratedType = (typeof CENTRALIS_GENERATED_TYPES)[number];
export type CentralisRole = (typeof CENTRALIS_MODIFICATION_ROLES)[number];

/**
 * Verifie si un type de document est non modifiable
 */
export const isNonModifiableType = (type: string): boolean => {
  const normalizedType = normalizeDocumentType(type);
  return ALL_NON_MODIFIABLE_TYPES.includes(
    normalizedType as AllNonModifiableType,
  );
};

/**
 * Verifie si un type de document est genere par Centralis
 */
export const isCentralisGeneratedType = (type: string): boolean => {
  const normalizedType = normalizeDocumentType(type);
  return CENTRALIS_GENERATED_TYPES.includes(
    normalizedType as CentralisGeneratedType,
  );
};

/**
 * Verifie si le role peut modifier les documents non modifiables
 */
export const canModifyNonModifiable = (role: string | undefined): boolean => {
  if (!role) return false;
  return CENTRALIS_MODIFICATION_ROLES.includes(role as CentralisRole);
};

/**
 * Determine le statut de permission pour un document
 */
export interface DocumentPermission {
  canUpload: boolean;
  canModify: boolean;
  message?: string;
  reason?: "non_modifiable" | "centralis_generated";
}

/**
 * Verifie les permissions pour un type de document
 */
export const checkDocumentPermission = (
  type: string,
  documentExists: boolean,
  userRole?: string,
): DocumentPermission => {
  // Document genere par Centralis
  if (isCentralisGeneratedType(type)) {
    if (canModifyNonModifiable(userRole)) {
      return { canUpload: true, canModify: true };
    }
    return {
      canUpload: false,
      canModify: false,
      message: DOCUMENT_MESSAGES.CENTRALIS_GENERATED,
      reason: "centralis_generated",
    };
  }

  // Document non modifiable
  if (isNonModifiableType(type)) {
    // Premier upload autorise
    if (!documentExists) {
      return { canUpload: true, canModify: false };
    }

    // Equipe Centralis peut modifier
    if (canModifyNonModifiable(userRole)) {
      return { canUpload: true, canModify: true };
    }

    // Utilisateur standard bloque
    return {
      canUpload: false,
      canModify: false,
      message: DOCUMENT_MESSAGES.NON_MODIFIABLE,
      reason: "non_modifiable",
    };
  }

  // Document standard
  return { canUpload: true, canModify: true };
};
